import { useState, useEffect } from "react";
import { Button, Col, FormGroup, Input, Row } from "reactstrap";
import EditPrefillMenu from "../../CRM/EditPrefillMenu";
import useModule from "hooks/useModule";
import AppDrawer from "../AppDrawer";
import AppSpinner from "../../../elements/AppSpinner";
import ModulePicker from "../../Modules/ModulePicker";
import StrUtils from "../../../utils/StrUtils";
import SelectionPage from "./SelectionPage";
import CreateFromGlobalPage from "./CreateFromGlobalPage";
import {
  GetSelectedLayout,
  ModuleLayoutSelector,
} from "../Records/ModuleLayoutSelector";

const icons = [
  "mdi mdi-plus-box",
  "mdi mdi-phone",
  "mdi mdi-email",
  "mdi mdi-microsoft-outlook",
  "mdi mdi-gmail",
  "mdi mdi-message-text",
  "mdi mdi-chat",
  "mdi mdi-message-video",
  "mdi mdi-microsoft-teams",
  "mdi mdi-calendar",
  "mdi mdi-calendar-account",
  "mdi mdi-account",
  "mdi mdi-account-multiple",
  "mdi mdi-account-group",
  "mdi mdi-account-cash",
  "mdi mdi-card-account-details",
  "mdi mdi-format-list-text",
  "mdi mdi-checkbox-multiple-marked",
  "mdi mdi-note",
  "mdi mdi-file-document",
  "mdi mdi-microsoft-onenote",
  "mdi mdi-microsoft-powerpoint",
  "mdi mdi-microsoft-excel",
  "mdi mdi-microsoft-word",
  "mdi mdi-google-spreadsheet",
  "mdi mdi-chart-box",
  "mdi mdi-aws",
  "mdi mdi-microsoft-azure",
  "mdi mdi-salesforce",
  "mdi mdi-google-cloud",
  "mdi mdi-handshake",
  "mdi mdi-clock",
  "mdi mdi-domain",
];

const CreateQuickCreateDrawer = ({
  open,
  onClose,
  onCreate,
  onUpdate,
  afterCreate,
  afterUpdate,
  moduleAPIName,
  recordModuleAPIName,
  updating,
  existingQuickCreate = null,
  size = "sm",
  placement = "right",
  variableIdentifiers = null,
  backdrop = true,
  fieldSchema,
  useModulePicker = false,
  existingQuickCreates = null,
  usePageSelection = true,
  handleAddGlobals,
  global = false,
  disableLayouts,
}) => {
  usePageSelection = existingQuickCreates ? usePageSelection : false;
  const [page, setPage] = useState("selection"); // "selection" | "normal" | "global"
  const [mAPIName, setModAPIName] = useState(moduleAPIName);
  const modAPIName = mAPIName || moduleAPIName;
  const { module } = useModule(modAPIName);
  const { module: originalModule } = useModule(recordModuleAPIName);
  const [layout, setLayout] = useState(null);
  const [title, setTitle] = useState("");
  const [icon, setIcon] = useState("mdi mdi-plus-box");
  const [sections, setSections] = useState([]);
  const [selectedQCS, setSelectedQCS] = useState({});
  const finalPage = existingQuickCreate
    ? "normal"
    : usePageSelection
    ? page
    : "normal";

  const isSectionsValid = () => {
    if (sections.length === 0) return false;

    for (const section of sections) {
      if (!section.Value && typeof section.Value !== "boolean") return false;
    }

    return true;
  };

  const onSuccess = () => {
    setTitle("");
    setSections([]);
    setIcon("mdi mdi-plus-box");
    setPage("selection");
    setSelectedQCS({});
    setLayout(null);

    existingQuickCreate ? afterUpdate() : afterCreate();
  };

  const handleCreate = () => {
    onCreate({
      key: "Prefill",
      payload: {
        ModuleAPIName: modAPIName,
        Name: title,
        PrefillPairs: sections,
        Icon: icon ?? icons[0],
        Layout: layout?.id,
      },
      onSuccess: onSuccess,
    });
  };

  const handleUpdate = () => {
    const payload = {
      ModuleAPIName: modAPIName,
      Name: title,
      PrefillPairs: sections,
      Icon: icon ?? icons[0],
    };

    if (layout) payload.Layout = layout?.id;

    onUpdate({
      id: existingQuickCreate.id,
      key: "Prefills",
      payload: payload,
      onSuccess: onSuccess,
    });
  };

  const handleClose = () => {
    if (!existingQuickCreate && usePageSelection && finalPage !== "selection") {
      return setPage("selection");
    }

    // Reset the state to the original values.
    setTitle("");
    setSections([]);
    setIcon("mdi mdi-plus-box");
    setPage("selection");
    setSelectedQCS({});
    setLayout(null);

    onClose();
  };

  const onAddGlobals = () => {
    const ids = Object.keys(selectedQCS);

    if (!ids.length) return;

    const globals = ids.map((id) => ({ id: StrUtils.uuidv4(), GlobalID: id }));

    const newPrefills = [...existingQuickCreates, ...globals];

    handleAddGlobals(newPrefills, onSuccess);
  };

  useEffect(() => {
    if (existingQuickCreate) {
      setTitle(existingQuickCreate.Name);
      setIcon(existingQuickCreate?.Icon ?? icons[0]);
      setSections(existingQuickCreate.PrefillPairs);
      setModAPIName(existingQuickCreate?.ModuleAPIName);
    } else {
      setTitle("");
      setSections([]);
      setIcon("mdi mdi-plus-box");
      setModAPIName("");
    }
  }, [existingQuickCreate]);

  useEffect(() => {
    if (!module) return;

    if (existingQuickCreate) {
      setLayout(GetSelectedLayout(module, existingQuickCreate?.Layout) || null);
    } else {
      setLayout(null);
    }
  }, [module, existingQuickCreate?.Layout]);

  useEffect(() => {
    if (!moduleAPIName) return;

    setModAPIName(moduleAPIName);
  }, [moduleAPIName]);

  const isNormal = finalPage === "normal";
  const isSelection = finalPage === "selection";

  return (
    <AppDrawer
      size={size}
      placement={placement}
      open={open}
      onClose={handleClose}
      backdrop={backdrop}
      headerTitle={`${
        existingQuickCreate ? "Edit" : isNormal || isSelection ? "New" : "Add"
      } Quick Create${!isNormal ? "s" : ""}`}
      headerOptions={
        !isSelection ? (
          <Button
            color="primary"
            size="sm"
            disabled={
              finalPage === "normal"
                ? !title || isSectionsValid() === false || updating
                : !Object.keys(selectedQCS).length
            }
            onClick={
              existingQuickCreate
                ? handleUpdate
                : finalPage === "normal"
                ? handleCreate
                : onAddGlobals
            }
            style={{ width: 90 }}
          >
            {updating ? (
              <AppSpinner size="sm" color="white" />
            ) : existingQuickCreate ? (
              "Update"
            ) : finalPage === "normal" ? (
              "Create"
            ) : (
              "Add"
            )}
          </Button>
        ) : undefined
      }
      bodyCSS="p-4"
      body={
        <>
          {finalPage === "selection" && (
            <SelectionPage setPage={setPage} type="Quick Create" />
          )}
          {finalPage === "global" && (
            <CreateFromGlobalPage
              selectedGlobals={selectedQCS}
              setSelectedGlobals={setSelectedQCS}
              existingValues={existingQuickCreates}
              moduleAPIName={moduleAPIName}
              globalType="QuickCreates"
            />
          )}
          {finalPage === "normal" && (
            <>
              {useModulePicker && (
                <FormGroup>
                  <h5>Module</h5>
                  <ModulePicker
                    onChange={(mod) => {
                      setModAPIName(mod);
                      setTitle("");
                      setSections([]);
                      setIcon("mdi mdi-plus-box");
                    }}
                    value={modAPIName}
                    disabled={!!existingQuickCreate}
                    cleanable={false}
                  />
                </FormGroup>
              )}
              <FormGroup>
                <h5>Name</h5>
                <Input
                  bsSize="sm"
                  placeholder={"Name"}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </FormGroup>
              <FormGroup>
                <h5>Icon</h5>
                <Row>
                  {icons?.map((ic, i) => (
                    <Col key={i} xs="auto" className="mb-3">
                      <Button
                        size="sm"
                        className="btn-icon-only"
                        color={icon === ic ? "primary" : "secondary"}
                        outline={icon !== ic}
                        onClick={() => {
                          setIcon(ic);
                        }}
                      >
                        <i className={ic}></i>
                      </Button>
                    </Col>
                  ))}
                </Row>
              </FormGroup>
              {!disableLayouts && (
                <FormGroup>
                  <h5 className="mb-0">Layout</h5>
                  <small>
                    Optionally enforce a specific layout when this quick create
                    is used
                  </small>
                  <ModuleLayoutSelector
                    module={module}
                    layout={layout}
                    setLayout={(newLayout) => setLayout(newLayout)}
                    onClear={() => setLayout(null)}
                    block
                    className="mt-2 mb-4"
                    hideOnOne={false}
                  />
                </FormGroup>
              )}
              <EditPrefillMenu
                module={module}
                originalModule={originalModule}
                prefillPairs={sections}
                onChange={setSections}
                existingPrefill={existingQuickCreate?.PrefillPairs}
                showVariables
                variableIdentifiers={
                  variableIdentifiers
                    ? variableIdentifiers
                    : global
                    ? ["user", "record"]
                    : ["user"]
                }
                editOnly
                multiSubitemAPIName={fieldSchema?.APIName}
                fieldSchema={fieldSchema}
              />
            </>
          )}
        </>
      }
    />
  );
};

export default CreateQuickCreateDrawer;
